import type {
  CompleteMessage,
  DishAnalysisMessage,
  ErrorMessage,
  Message,
  ProgressMessage,
  MessageContent as TMessageContent,
  TextMessage,
  WarningMessage,
  WineRecommendationsMessage,
  WineSearchResultsMessage,
} from '@/common/db-typebox';
import type { Stage } from '@/common/db-typebox/chat.basic.typebox';
import { Card } from '@/pages/live-chat/components/card';
import { AlertCircle, AlertTriangle } from 'lucide-react';
import { useMemo } from 'react';
import { DishAnalysisCard } from './dish-analysis-card';
import { WineRecommendationCard } from './wine-recommendation-card';
import { WineSearchResults } from './wine-search-results';

function ProgressMessageContent({
  message,
  stage,
  messages,
  currentTimestamp,
}: {
  message: string;
  stage: Stage;
  messages: Message[];
  currentTimestamp: string;
}) {
  // Check if this stage is complete by looking for a newer message of a different stage
  // or a non-progress message
  const isComplete = useMemo(() => {
    return messages.some((msg) => {
      const msgTime = new Date(msg.content.timestamp);
      const currentTime = new Date(currentTimestamp);

      if (msgTime <= currentTime) return false;

      if (isProgressMessage(msg.content)) {
        return msg.content.structured_content?.progress?.stage !== stage;
      }

      // Any non-progress message after this one means this stage is complete
      return true;
    });
  }, [messages, stage, currentTimestamp]);

  return (
    <div className="flex justify-left my-2">
      <div className="flex items-center gap-3 px-4 py-2 bg-muted/50 rounded-full text-muted-foreground">
        {isComplete ? (
          <div className="w-5 h-5 text-green-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
              role="img"
              aria-labelledby="checkmark-title"
            >
              <title id="checkmark-title">Stage complete</title>
              <path d="M20 6L9 17l-5-5" />
            </svg>
          </div>
        ) : (
          <div className="flex items-center gap-1">
            <div className="w-1.5 h-1.5 bg-current rounded-full animate-[pulse_1s_ease-in-out_infinite]" />
            <div className="w-1.5 h-1.5 bg-current rounded-full animate-[pulse_1s_ease-in-out_0.2s_infinite]" />
            <div className="w-1.5 h-1.5 bg-current rounded-full animate-[pulse_1s_ease-in-out_0.4s_infinite]" />
          </div>
        )}
        <div className="flex flex-col">
          <span className="text-sm font-medium">{message}</span>
          <span className="text-xs opacity-70">{stage}</span>
        </div>
      </div>
    </div>
  );
}

function WarningMessageContent({
  title,
  message,
}: { title: string; message: string }) {
  return (
    <div className="flex justify-start my-2">
      <div className="flex items-start gap-3 max-w-[80%] px-4 py-3 bg-yellow-100/50 text-yellow-800 dark:bg-yellow-900/20 dark:text-yellow-200 rounded-xl border border-yellow-200/50 dark:border-yellow-700/30">
        <AlertTriangle className="h-5 w-5 shrink-0 mt-0.5" />
        <div className="flex flex-col gap-1">
          <span className="font-medium">{title}</span>
          <p className="text-sm text-yellow-800/90 dark:text-yellow-200/90 whitespace-pre-wrap break-words">
            {message}
          </p>
        </div>
      </div>
    </div>
  );
}

interface MessageContentProps {
  message: Message;
  messages: Message[];
  onError: (error: string) => void;
}

// Type guards
export function isProgressMessage(
  message: TMessageContent,
): message is ProgressMessage {
  return message.response_type === 'progress';
}

export function isDishAnalysisMessage(
  message: TMessageContent,
): message is DishAnalysisMessage {
  return message.response_type === 'dish_analysis';
}

export function isWineSearchResultsMessage(
  message: TMessageContent,
): message is WineSearchResultsMessage {
  return message.response_type === 'wine_search_results';
}

export function isWineRecommendationsMessage(
  message: TMessageContent,
): message is WineRecommendationsMessage {
  return message.response_type === 'wine_recommendations';
}

export function isTextMessage(
  message: TMessageContent,
): message is TextMessage {
  return message.response_type === 'text';
}

export function isWarningMessage(
  message: TMessageContent,
): message is WarningMessage {
  return message.response_type === 'warning';
}

export function isErrorMessage(
  message: TMessageContent,
): message is ErrorMessage {
  return message.response_type === 'error';
}

export function isCompleteMessage(
  message: TMessageContent,
): message is CompleteMessage {
  return message.response_type === 'complete';
}

export function MessageContent({
  message,
  messages,
  onError,
}: MessageContentProps) {
  const content = message.content;

  // Find if there's a loading message for search that's newer than this message
  const { isSearching, isGeneratingRecommendations } = useMemo(() => {
    // Find the latest search progress message after current message
    const laterMessages = messages.filter(
      (m) => new Date(m.content.timestamp) > new Date(content.timestamp),
    );

    // Check if there's an incomplete search in progress
    const searchInProgress = laterMessages.some(
      (m) =>
        isProgressMessage(m.content) &&
        m.content.structured_content?.progress?.stage === 'search',
    );

    // Check if search was completed (any non-progress message or different stage progress message)
    const searchCompleted = laterMessages.some(
      (m) =>
        !isProgressMessage(m.content) ||
        (isProgressMessage(m.content) &&
          m.content.structured_content?.progress?.stage !== 'search'),
    );

    // Similar logic for recommendations
    const recInProgress = laterMessages.some(
      (m) =>
        isProgressMessage(m.content) &&
        m.content.structured_content?.progress?.stage === 'recommendation',
    );
    const recCompleted = laterMessages.some(
      (m) =>
        !isProgressMessage(m.content) ||
        (isProgressMessage(m.content) &&
          m.content.structured_content?.progress?.stage !== 'recommendation'),
    );

    return {
      isSearching: searchInProgress && !searchCompleted,
      isGeneratingRecommendations: recInProgress && !recCompleted,
    };
  }, [messages, content.timestamp]);

  // Handle text message
  if (isTextMessage(content)) {
    const isUser = content.role !== 'assistant';
    const messageDate = new Date(content.timestamp);
    const today = new Date();
    const isToday = messageDate.toDateString() === today.toDateString();
    const isCurrentYear = messageDate.getFullYear() === today.getFullYear();

    const timestamp = isToday
      ? messageDate.toLocaleTimeString([], {
          hour: '2-digit',
          minute: '2-digit',
        })
      : isCurrentYear
        ? messageDate.toLocaleDateString([], {
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          })
        : messageDate.toLocaleDateString([], {
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit',
          });

    return (
      <div className={`flex ${isUser ? 'justify-end' : 'justify-start'}`}>
        <div
          className={`max-w-[80%] rounded-2xl px-4 py-2 ${
            isUser
              ? 'bg-primary text-primary-foreground'
              : 'bg-muted text-foreground'
          }`}
        >
          <p className="text-sm whitespace-pre-wrap break-words">
            {content.content}
          </p>
          {isUser && (
            <p className="text-xs opacity-70 mt-1 text-right">{timestamp}</p>
          )}
        </div>
      </div>
    );
  }

  // Handle error message
  if (isErrorMessage(content)) {
    return (
      <div className="flex justify-start my-2">
        <div className="flex items-start gap-3 max-w-[80%] px-4 py-3 bg-destructive/10 text-destructive rounded-xl border border-destructive/20">
          <AlertCircle className="h-5 w-5 shrink-0 mt-0.5" />
          <div className="flex flex-col gap-1">
            <span className="font-medium">
              {content.structured_content?.title}
            </span>
            <p className="text-sm text-destructive/90 whitespace-pre-wrap break-words">
              {content.structured_content?.message}
            </p>
          </div>
        </div>
      </div>
    );
  }

  // Handle progress message
  if (isProgressMessage(content) && content.structured_content?.progress) {
    const { progress } = content.structured_content;
    return (
      <ProgressMessageContent
        message={progress.message}
        stage={progress.stage}
        messages={messages}
        currentTimestamp={content.timestamp}
      />
    );
  }

  // Handle dish analysis
  if (isDishAnalysisMessage(content) && content.structured_content?.analysis) {
    const { analysis } = content.structured_content;
    return <DishAnalysisCard analysis={analysis} />;
  }

  // Handle wine search results
  if (
    isWineSearchResultsMessage(content) &&
    content.structured_content?.search_results
  ) {
    const { search_results } = content.structured_content;
    return (
      <WineSearchResults
        products={search_results}
        isLoading={isSearching}
        onError={(error) => onError(error.message)}
      />
    );
  }

  // Handle wine recommendations
  if (
    isWineRecommendationsMessage(content) &&
    content.structured_content?.recommendations
  ) {
    const { recommendations, summary } = content.structured_content;

    return (
      <div
        className={`space-y-4 ${isGeneratingRecommendations ? 'opacity-50' : ''}`}
      >
        {/* Primary Recommendation */}
        {recommendations.slice(0, 1).map((rec, index) => (
          <WineRecommendationCard
            key={`${rec.name}-${index}`}
            recommendation={rec}
            displayType="primary"
          />
        ))}

        {/* Alternative Recommendations */}
        {recommendations.length > 1 && (
          <Card className="p-4 border-2 border-primary/30 shadow-md relative space-y-4">
            <div className="absolute -top-3 left-4 bg-amber-100 text-amber-700 px-2 py-0.5 text-sm rounded-full font-medium">
              Alternative Recommendations
            </div>
            {recommendations.slice(1).map((rec, index) => (
              <WineRecommendationCard
                key={`${rec.name}-${index + 1}`}
                title={`Alternative ${index + 1}`}
                recommendation={rec}
                displayType="alternative"
              />
            ))}
          </Card>
        )}

        {/* Summary */}
        {summary && (
          <Card className="p-4 border-2 border-primary/30 shadow-md relative space-y-4">
            <div className="absolute -top-3 left-4 bg-green-100 text-green-700 px-2 py-0.5 text-sm rounded-full font-medium">
              Summary
            </div>
            {summary.highlights.map((highlight) => (
              <p key={highlight} className="text-sm text-muted-foreground">
                {highlight}
              </p>
            ))}
          </Card>
        )}
      </div>
    );
  }

  // Handle warning message
  if (isWarningMessage(content) && content.structured_content) {
    const { title, message: warning_message } = content.structured_content;
    return <WarningMessageContent title={title} message={warning_message} />;
  }

  // Handle complete message
  if (isCompleteMessage(content)) {
    return null; // Complete messages don't need visual representation
  }

  // Fallback for unknown message types
  return <p className="text-sm">{content.content}</p>;
}
